import { Icon } from '../../design-system/icons';
import { dsColors } from '../../design-system/tokens/colors';
import { PricingHero } from './PricingHero';
import { PlanCard } from './PlanCard';
import {
  BillingToggle,
  CloseButton,
  PlanSelector,
  SubscribeButton,
} from './PricingControls';
import { badgeDelPlan, LinkPlanesDeEquipo, type PricingCatalogProps } from './PricingCatalog';
import { BannerDePromo, promoDeLosPlanes } from './BannerDePromo';
import type { PricingPlanKey } from './pricingContent';

/**
 * Paywall en **desktop** — modal centrado de dos paneles. Ver ADR-2026-07-29-desktop-paywall-modal-dos-paneles (modal de
 * dos paneles, supersede las 3 columnas de ADR-2026-07-28-desktop-3-columnas-vez-selector), ADR-2026-07-29-reparto-contenido-modal-desktop-carrusel (reparto del
 * contenido entre paneles) y ADR-2026-07-30-iteracion-modal-desktop-solo-eje (esta iteración).
 *
 * - **Izquierda:** el carrusel y nada más — imagen, título, bajada y los pasos,
 *   alineados a la izquierda como en el onboarding. Es el argumento de producto,
 *   y no depende del plan elegido.
 * - **Derecha:** todo lo del plan, en la **misma estructura que mobile** — los
 *   dos segmentados apilados (Mensual/Anual arriba, Básico/Pro/Ultra debajo),
 *   la `PlanCard` del plan elegido y el CTA.
 *
 * El modal tiene **alto fijo**: si midiera por contenido, cambiar de plan lo
 * haría saltar (Básico tiene 4 features, Ultra 2 + el slider de tiers). Está
 * dimensionado para que el plan más largo (Ultra) entre entero sin scroll; el
 * `overflow-y-auto` del bloque de la card queda sólo como red para pantallas
 * bajas (notebooks de 720p), donde algo tiene que ceder.
 */
export function PricingDesktop<K extends string = PricingPlanKey>({
  plans,
  selected,
  onSelect,
  onSubscribe,
  processing,
  onClose,
  ultraTier,
  showBillingToggle,
  annual,
  onAnnualChange,
  note,
  error,
  onHelp,
  slides,
  skipLabel,
  onSkip,
  contextCopy,
  animateIn = false,
  variant = 'screen',
  onVerPlanesDeEquipo,
  textoLinkAlPie,
}: PricingCatalogProps<K>) {
  const current = plans.find((p) => p.key === selected) ?? plans[0];
  const planKeys = plans.map((p) => p.key);
  /**
   * `embedded` ya no llega acá: desde ADR-2026-07-31-embebido-deja-ser-modal-scrim lo atiende `PricingComparativa`, con
   * los tres planes en fila. Se conserva la rama porque el envoltorio sin `fixed`
   * ni scrim es lo único que hace falta si alguna vez se quiere el modal de dos
   * paneles dentro de una página.
   */
  const embebido = variant === 'embedded';
  /**
   * `screen` es una PANTALLA, no una capa — ADR-2026-07-31-vuelve-pantalla-completa-overlay-queda.
   *
   * Es lo que renderiza `/subscription`, que es una **ruta**: el usuario llegó ahí
   * navegando, no le apareció algo encima de lo que estaba haciendo. Presentarla
   * como card flotante sobre un scrim la hacía leer como un overlay que tapa la app
   * y que se cierra tocando afuera, cuando en realidad es su propia página.
   *
   * `sheet` sí es capa: lo abren el intento de generar sin créditos y los botones de
   * mejora, sobre una pantalla que se queda donde está. Ese conserva el scrim y la
   * card centrada.
   */
  const pantallaCompleta = variant === 'screen';

  // Con el switch en "Anual" la card muestra las cifras anuales. Es lo que antes
  // cargaban las tarjetas de "Período de facturación" que este switch reemplaza:
  // sin esto, elegir Anual no cambiaría nada en pantalla. Siguen siendo cifras
  // derivadas del mensual (ADR-2026-07-28-switch-mensual-anual-visual-solo), no un precio real del backend.
  const verAnual = showBillingToggle && annual;

  return (
    // `pricing-cursor-scope`: manito custom sobre lo clickeable. El cursor custom
    // de la app le gana a `cursor-pointer`, así que se pide desde `index.css` (ADR-2026-07-30-switch-opaco-sobre-panel-blanco).
    //
    // `page-fade-in` (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo) es LA MISMA clase que usa el cambio de página
    // (ADR-2026-07-31-fade-entre-paginas-toda-app), no una copia de sus valores: compartirla es lo único que garantiza
    // que sigan iguales si se ajusta la curva. Va en el contenedor de afuera para
    // que el scrim y el modal entren juntos —animar sólo el modal deja el fondo
    // apareciendo de golpe—. No necesita el doble rAF de `usePageFade`: es una
    // animación CSS, corre sola al montar.
    //
    // Es OPT-IN: este componente lo comparten los overlays y `/subscription`, que es
    // una ruta dentro de `DashboardLayout` y por lo tanto YA se desvanece con el fade
    // global. Aplicarlo siempre le anidaría dos animaciones de 680ms, que se
    // multiplican y dejan esa pantalla más oscura y más lenta al llegar.
    <div
      className={
        embebido
          ? 'pricing-cursor-scope flex w-full justify-center'
          : `pricing-cursor-scope fixed inset-0 z-[200] flex items-center justify-center ${
              // Sin `p-4` a pantalla completa: ese padding es el aire que separa la
              // card flotante del borde, y acá no hay card que separar.
              pantallaCompleta ? '' : 'p-4'
            } ${animateIn ? 'page-fade-in' : ''}`
      }
    >
      {/* Fondo: el modal flota sobre la app, que sigue a la vista atenuada. No va
          ni embebido (no hay nada detrás que atenuar) ni a pantalla completa (el
          contenido tapa el 100%, así que el scrim no se vería nunca). */}
      {!embebido && !pantallaCompleta && (
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden
        />
      )}

      {/* Alto FIJO (no `h-fit`): con alto por contenido, cambiar de plan movía el
          modal entero y el CTA saltaba bajo el cursor.
          El tope pasó de 800px a 860px (ADR-2026-07-31-menu-cuenta-avatar-uno-solo). Los 800 salían de medir el caso
          más alto de entonces —Ultra con cupón, 789px—, pero esa medición quedó
          vieja cuando el copy por contexto (ADR-2026-07-30-copy-catalogo-segun-contexto-abrio) sumó título y bajada al panel:
          con `contextCopy` el contenido pedía ~835px y la card quedaba recortada por
          abajo. Se le da el alto en vez de comprimir el texto, que es justo lo que
          se pidió que respirara. Sigue habiendo tope y no `100vh` a secas: en
          pantallas muy altas el modal se estiraría sólo para agregar blanco. */}
      <div
        // Embebido no es un diálogo: no atrapa el foco ni tapa la pantalla, así
        // que anunciarlo como modal sería mentirle al lector de pantalla.
        // Ni embebido ni a pantalla completa son diálogos: no atrapan el foco ni
        // tapan algo que siga vivo detrás, así que anunciarlos como modales sería
        // mentirle al lector de pantalla. `/subscription` es una ruta.
        role={embebido || pantallaCompleta ? 'region' : 'dialog'}
        aria-modal={embebido || pantallaCompleta ? undefined : true}
        aria-label="Elegí tu plan"
        className={`relative grid w-full grid-cols-[minmax(0,0.82fr)_minmax(0,1fr)] overflow-hidden bg-white ${
          pantallaCompleta
            ? // Llena el viewport: sin tope de ancho, sin bordes redondeados y sin
              // sombra. El `min-h` tampoco va — a pantalla completa el alto lo da
              // el viewport, y un piso de 520px sólo forzaría scroll en pantallas
              // más bajas que eso.
              'h-full'
            : `min-h-[520px] max-w-[1040px] rounded-3xl ${
                embebido
                  ? // Contra el viewport igual que el overlay, pero descontando el
                    // encabezado de Configuración; el `min-h` de arriba sigue siendo
                    // el piso, que es lo que garantiza que Ultra entre.
                    'h-[min(calc(100vh-13rem),860px)] border border-neutral-200'
                  : 'h-[min(calc(100vh-2rem),860px)] shadow-2xl'
              }`
        }`}
      >
        {/* ── Panel del carrusel ── */}
        {/* Sólo la imagen con su título, bajada y pasos: es el argumento de
            producto y no cambia con el plan elegido, así que nada de acá se
            re-renderiza al tocar el selector. */}
        <aside className="relative overflow-hidden bg-black">
          <PricingHero className="h-full" heightClassName="h-full" copyAlign="left" slides={slides} />
        </aside>

        {/* ── Panel del plan ── */}
        {/* `min-h-0` en la columna y en el bloque scrolleable: sin eso el grid
            deja crecer al hijo y el scroll interno nunca se activa. */}
        {/* `gap-2` con `contextCopy`: el copy suma una fila y, en cuentas
            `dev_tester` (las únicas con el switch de período), Ultra se pasaba 17px
            del alto disponible. Apretar los gaps recupera ~20px y el plan más
            largo vuelve a entrar sin scroll. */}
        {/* Con `contextCopy` el panel arranca con un título de 22px, y a 8px del
            borde se leía pegado al techo del modal (ADR-2026-07-31-menu-cuenta-avatar-uno-solo). Sube a 24px de aire
            arriba y el encabezado respira con 10px hasta el switch. El alto que eso
            cuesta lo devuelven los textos descriptivos de `PlanCard`, que bajaron a
            13px; ver ahí por qué el presupuesto de alto de este panel es ajustado. */}
        <section
          className={`flex min-h-0 flex-col px-8 pb-5 ${
            contextCopy ? 'gap-2.5 pt-6' : 'gap-3 pt-2.5'
          }`}
        >
          {/* Fila superior: el switch de período y, a su derecha, la cruz de
              cerrar —dentro del panel y sin fondo—. Comparten fila en vez de ir
              una debajo de la otra porque cada fila cuesta ~44px de alto, y con
              los dos segmentados el plan más largo dejaba de entrar. Cuando el
              switch no va (todos menos `dev_tester`, ADR-2026-07-28-switch-mensual-anual-visual-solo), el espaciador
              mantiene la cruz pegada a la derecha. */}
          {/* Con `contextCopy` (ADR-2026-07-30-copy-catalogo-segun-contexto-abrio) el copy del momento ocupa esta fila y el
              switch baja a la suya: el pedido es que el título vaya ARRIBA del
              Mensual/Anual, y dejarlo en un renglón propio con la cruz sola
              costaba otros ~44px de alto.
              El padding es SIMÉTRICO (`px-10`, no `pr-10`): la cruz va absoluta a
              la derecha, y reservando su lugar sólo de ese lado el texto centrado
              quedaba corrido a la izquierda respecto del panel. */}
          <div
            className={`relative flex min-h-[34px] shrink-0 ${
              contextCopy
                ? 'items-start justify-center px-10'
                : 'items-center justify-center'
            }`}
          >
            {contextCopy ? (
              <div className="flex w-full min-w-0 flex-col gap-1 text-center">
                <h2
                  className="text-[22px] font-[700] leading-[28px]"
                  style={{ color: dsColors.neutral[900] }}
                >
                  {contextCopy.title}
                </h2>
                <p
                  className="text-[13px] font-[500] leading-[18px]"
                  style={{ color: dsColors.neutral[600] }}
                >
                  {contextCopy.description}
                </p>
              </div>
            ) : /* Mismo switch que en mobile —misma forma, mismos colores de texto—,
                  con el vidrio vuelto opaco: sobre el panel blanco el translúcido
                  componía gris medio y la etiqueta inactiva era ilegible (ADR-2026-07-30-switch-opaco-sobre-panel-blanco).
                  Va centrado, como allá. */
            showBillingToggle ? (
              <BillingToggle
                annual={annual}
                onChange={onAnnualChange}
                variant="onPanel"
              />
            ) : null}
            <div
              className={`absolute right-0 -mr-1.5 flex ${
                contextCopy ? 'top-0 items-start' : 'inset-y-0 items-center'
              }`}
            >
              <CloseButton onClose={onClose} variant="plain" />
            </div>
          </div>

          {/* El switch, cuando el copy le ocupó la fila de arriba. */}
          {contextCopy && showBillingToggle ? (
            <div className="flex shrink-0 justify-center">
              <BillingToggle
                annual={annual}
                onChange={onAnnualChange}
                variant="onPanel"
              />
            </div>
          ) : null}

          {/* `pt-[18px]`: el badge "Popular" sobresale 18px por encima del
              riel; con menos aire se monta sobre lo que tenga arriba. */}
          <div className="shrink-0 pt-[18px]">
            <PlanSelector
              selected={selected}
              onSelect={onSelect}
              planKeys={planKeys}
              items={plans.map((p) => ({ key: p.key, tab: p.content.tab, badge: badgeDelPlan(p, true) }))}
              variant="onLight"
            />
          </div>

          {error ? (
            <div
              className="shrink-0 rounded-xl px-4 py-3 text-[13px] leading-[18px]"
              style={{ background: '#FEE2E2', color: '#991B1B' }}
            >
              {error}
            </div>
          ) : null}

          {/* Lo único que cambia de alto entre planes vive acá adentro.
              La card mide por CONTENIDO y arranca ARRIBA, pegada al selector de
              planes: es la continuación de lo que se acaba de elegir, y separada
              del selector por un hueco variable se leía como un bloque suelto
              (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil). Lo que sobra queda abajo, entre la card y el CTA.

              Sigue midiendo por contenido y no `min-h-full`: estirada, los planes
              cortos quedaban con un hueco blanco enorme ADENTRO de la card, que se
              lee como un error de maqueta (ADR-2026-07-30-ajustes-sobre-adr-815-switch). */}
          <div className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1">
            {/* Promo de temporada (Cyber Monday…). Va DENTRO del scroll: el alto del
                panel es justo, y fuera de él empujaría el CTA. Con el anual a la vista
                no va, como el tachado de la card. */}
            {!verAnual ? (
              <BannerDePromo
                {...promoDeLosPlanes(plans)}
                forma="fila"
                className="mb-3 shrink-0"
              />
            ) : null}
            <PlanCard
              compact
              content={current.content}
              credits={current.credits}
              creditsText={current.creditsText}
              creditsDetail={current.creditsDetail}
              generates={current.generates}
              price={verAnual ? current.annual.perMonth : current.price}
              period={verAnual ? 'por mes' : current.period}
              // El total anual va en su propia línea: sumado al período en la
              // misma daba un renglón largo que competía con el precio grande.
              priceNote={
                verAnual ? `${current.annual.total} facturado por año` : current.priceNote
              }
              strikePrice={verAnual ? undefined : current.strikePrice}
              discountLabel={verAnual ? undefined : current.discountLabel}
              ivaIncluido={current.ivaIncluido}
              isCurrent={current.isCurrent}
              ultraTier={current.tierSlider ?? (current.key === 'enterprise' ? ultraTier : undefined)}
            />
          </div>

          <div className="flex shrink-0 flex-col items-center gap-2.5">
            <SubscribeButton
              label={current.ctaLabel}
              onClick={() => onSubscribe(current.key)}
              disabled={current.ctaDisabled}
              loading={processing}
            />

            {/* Baja de la suscripción — única entrada al flujo de cancelación. */}
            {current.secondaryLabel && current.onSecondary ? (
              <button
                type="button"
                onClick={current.onSecondary}
                className="text-[13px] font-[600] hover:underline"
                style={{ color: dsColors.neutral[500] }}
              >
                {current.secondaryLabel}
              </button>
            ) : null}

            {/* "Omitir por ahora": cuando el catálogo interrumpe un flujo, es la
                salida que continúa ese flujo (distinta de cerrar). ADR-2026-07-29-presentacion-bottom-sheet-mobile-tocar. */}
            {skipLabel && onSkip ? (
              <button
                type="button"
                onClick={onSkip}
                className="text-[13px] font-[600] hover:underline"
                style={{ color: dsColors.neutral[500] }}
              >
                {skipLabel}
              </button>
            ) : null}

            {/* La letra chica va DEBAJO del CTA. `note` viene armado para el sheet
                azul de mobile y trae además botones de WhatsApp con colores sobre
                oscuro (`text-white/90`), invisibles en este panel blanco: se
                esconden y abajo va la versión clara. El texto sí se conserva. */}
            {note ? (
              <div
                className="text-center text-[12px] leading-[17px] [&_a]:hidden [&_button]:hidden"
                style={{ color: dsColors.neutral[500] }}
              >
                {note}
              </div>
            ) : null}

            {/* Ayuda por WhatsApp, con la misma forma de píldora que en mobile.
                Reemplaza al flotante de arriba a la derecha, que se quitó. El
                contenedor decide qué hace: acá, en una compu, abre el modal con QR. */}
            {onHelp ? (
              <button
                type="button"
                onClick={onHelp}
                className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[13px] font-[600] transition-colors hover:bg-black/[0.03]"
                style={{ borderColor: dsColors.neutral[200], color: dsColors.neutral[700] }}
              >
                <Icon name="outline/whatsapp" size={16} className="shrink-0" />
                ¿Tenés alguna duda? Escribinos
              </button>
            ) : null}
            {onVerPlanesDeEquipo ? <LinkPlanesDeEquipo onClick={onVerPlanesDeEquipo} texto={textoLinkAlPie} /> : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PricingDesktop;
